import type { ReactElement } from 'react';
import { Button, FieldError, Group, Input, Label, NumberField, Text } from 'react-aria-components';
import { Icon } from '../ui';
import f from '../ui/fields.module.css';
import s from './Settings.module.css';

export interface NumberInputProps {
  label: string;
  /** `null` when empty. */
  value: number | null;
  onChange: (value: number | null) => void;
  minValue?: number;
  maxValue?: number;
  /** Digits after the point (0 for whole numbers). */
  decimals?: number;
  /** − and + buttons either side (a stepper), each a 44 px target. */
  stepButtons?: boolean;
  description?: string;
  errorMessage?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
}

/** A labelled number input: whole counts and days, or a rate with up to two decimals. */
export function NumberInput({
  label,
  value,
  onChange,
  minValue,
  maxValue,
  decimals = 0,
  stepButtons = false,
  description,
  errorMessage,
  isRequired = false,
  isDisabled = false,
}: NumberInputProps): ReactElement {
  return (
    <NumberField
      className={f.field}
      value={value === null ? Number.NaN : value}
      onChange={(v) => {
        onChange(Number.isNaN(v) ? null : v);
      }}
      {...(minValue !== undefined && { minValue })}
      {...(maxValue !== undefined && { maxValue })}
      step={decimals === 0 ? 1 : 1 / 10 ** decimals}
      formatOptions={{ maximumFractionDigits: decimals, useGrouping: true }}
      isRequired={isRequired}
      isDisabled={isDisabled}
    >
      <Label className={f.label}>{label}</Label>
      <div className={f.body}>
        {stepButtons ? (
          <Group className={s.stepper}>
            <Button slot="decrement" className={s.stepButton}>
              <Icon name="minus" size={18} />
            </Button>
            <Input className={`${f.input} ${s.stepInput}`} />
            <Button slot="increment" className={s.stepButton}>
              <Icon name="plus" size={18} />
            </Button>
          </Group>
        ) : (
          <Input className={f.input} />
        )}
        {description !== undefined && (
          <Text slot="description" className={f.description}>
            {description}
          </Text>
        )}
        <FieldError className={f.error}>{errorMessage}</FieldError>
      </div>
    </NumberField>
  );
}
